import { supabase } from './supabase';
import {
    Domain,
    DomainGroup,
    DomainGroupMapping,
    CreateDomainInput,
    UpdateDomainInput,
    CreateDomainGroupInput,
    UpdateDomainGroupInput
} from '../types/domain';

export const domainService = {
    // Domain operations
    async getAllDomains(): Promise<Domain[]> {
        const { data, error } = await supabase
            .from('domains')
            .select('*')
            .order('name');
        
        if (error) throw error;
        return data;
    },

    async getDomainById(id: string): Promise<Domain> {
        const { data, error } = await supabase
            .from('domains')
            .select('*')
            .eq('id', id)
            .single();
        
        if (error) throw error;
        return data;
    },

    async getDomainByHostname(hostname: string): Promise<Domain> {
        const { data, error } = await supabase
            .from('domains')
            .select('*')
            .eq('hostname', hostname)
            .single();
        
        if (error) throw error;
        return data;
    },

    async createDomain(domain: CreateDomainInput): Promise<Domain> {
        const { data, error } = await supabase
            .from('domains')
            .insert(domain)
            .select()
            .single();
        
        if (error) throw error;
        return data;
    },

    async updateDomain(domain: UpdateDomainInput): Promise<Domain> {
        const { id, ...updates } = domain;
        const { data, error } = await supabase
            .from('domains')
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        
        if (error) throw error;
        return data;
    },

    async deleteDomain(id: string): Promise<void> {
        const { error } = await supabase
            .from('domains')
            .delete()
            .eq('id', id);
        
        if (error) throw error;
    },

    // Domain group operations
    async getAllDomainGroups(): Promise<DomainGroup[]> {
        const { data, error } = await supabase
            .from('domain_groups')
            .select('*')
            .order('name');
        
        if (error) throw error;
        return data;
    },

    async getDomainGroupById(id: number): Promise<DomainGroup> {
        const { data, error } = await supabase
            .from('domain_groups')
            .select('*')
            .eq('id', id)
            .single();
        
        if (error) throw error;
        return data;
    },

    async createDomainGroup(group: CreateDomainGroupInput): Promise<DomainGroup> {
        const { data, error } = await supabase
            .from('domain_groups')
            .insert(group)
            .select()
            .single();
        
        if (error) throw error;
        return data;
    },

    async updateDomainGroup(group: UpdateDomainGroupInput): Promise<DomainGroup> {
        const { id, ...updates } = group;
        const { data, error } = await supabase
            .from('domain_groups')
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        
        if (error) throw error;
        return data;
    },

    async deleteDomainGroup(id: number): Promise<void> {
        const { error } = await supabase
            .from('domain_groups')
            .delete()
            .eq('id', id);
        
        if (error) throw error;
    },

    // Domain group mapping operations
    async addDomainToGroup(domainId: string, groupId: number): Promise<DomainGroupMapping> {
        const { data, error } = await supabase
            .from('domain_group_mappings')
            .insert({
                domain_id: domainId,
                group_id: groupId
            })
            .select()
            .single();
        
        if (error) throw error;
        return data;
    },

    async removeDomainFromGroup(domainId: string, groupId: number): Promise<void> {
        const { error } = await supabase
            .from('domain_group_mappings')
            .delete()
            .match({
                domain_id: domainId,
                group_id: groupId
            });
        
        if (error) throw error;
    },

    async getDomainsInGroup(groupId: number): Promise<Domain[]> {
        const { data, error } = await supabase
            .from('domains')
            .select('*, domain_group_mappings!inner(*)')
            .eq('domain_group_mappings.group_id', groupId)
            .order('name');
        
        if (error) throw error;
        return data;
    }
}; 